import React, { useState, useRef, useEffect } from 'react';
import { Droplets, Sprout, Flower2, Scissors, X, Loader2, Camera, ImagePlus, Pencil, Star, Split, FileDown, Check, Share2, ArrowUpDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { API_BASE_URL } from '../utils/apiBase';
import {
  addLocalCareLogEntry,
  deleteLocalCareLogEntry,
  attachLocalCareLogPhoto,
  editLocalCareLogEntryNote,
  promoteLocalCareLogPhotoToCover,
  getPhoto as getLocalPhoto,
} from '../services/localPlantStore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { exportCareTimelineToPDF } from '../utils/pdfExport';
import { shareCareTimeline } from '../utils/shareCareTimeline';

/**
 * Care Log: one-tap logging for the four common plant chores + a reverse-
 * chronological timeline of recent actions. Storage lives on `plant.care_log`
 * (array of `{entry_id, action, at, note?, photo_drive_id?}`). Backend caps
 * at 100 entries per plant; the frontend just renders whatever comes back.
 *
 * Each entry can optionally have a photo attached — either a Drive file id
 * (auth mode) or an IndexedDB key (local mode). Both modes use the same
 * `photo_drive_id` field on the entry so the render path is unified; only
 * the fetch differs. When 2+ entries have photos, a "Growth timeline"
 * horizontal-scroll strip appears above the timeline for at-a-glance
 * visual progress.
 */
// AA-compliant colors: darkened `colorFg` so 14 px/500 action labels sit
// above 4.5:1 on the light background pastel (`colorBg`).
const ACTIONS = [
  { key: 'watered', icon: Droplets, colorFg: 'hsl(200, 60%, 32%)', colorBg: 'hsl(200, 60%, 95%)' },
  { key: 'fertilized', icon: Sprout, colorFg: 'hsl(85, 55%, 28%)', colorBg: 'hsl(85, 55%, 95%)' },
  { key: 'repotted', icon: Flower2, colorFg: 'hsl(21, 70%, 32%)', colorBg: 'hsl(21, 70%, 95%)' },
  { key: 'pruned', icon: Scissors, colorFg: 'hsl(280, 40%, 42%)', colorBg: 'hsl(280, 40%, 95%)' },
];

// Locale-aware "2 days ago" for the timeline rows.
function relTime(iso, language) {
  try {
    const rtf = new Intl.RelativeTimeFormat(language || 'en', { numeric: 'auto' });
    const diff = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
    const abs = Math.abs(diff);
    if (abs < 60) return rtf.format(Math.round(diff), 'second');
    if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
    if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
    if (abs < 2592000) return rtf.format(Math.round(diff / 86400), 'day');
    if (abs < 31536000) return rtf.format(Math.round(diff / 2592000), 'month');
    return rtf.format(Math.round(diff / 31536000), 'year');
  } catch (_e) {
    return new Date(iso).toLocaleDateString();
  }
}

/**
 * Small hook that resolves an entry's photo_drive_id to a renderable data URL
 * (local mode) or an authorized blob URL (Drive mode). Kept inline instead of
 * a separate util because it's specific to this component's lifecycle — a
 * blob URL is revoked on unmount to avoid a memory leak.
 */
function useCareLogPhoto(photoDriveId, isLocalMode) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!photoDriveId) { setSrc(null); return undefined; }
    let cancelled = false;
    let localBlobUrl = null;
    (async () => {
      try {
        if (isLocalMode) {
          const base64 = await getLocalPhoto(photoDriveId);
          if (!cancelled && base64) {
            setSrc(`data:image/jpeg;base64,${base64}`);
          }
        } else {
          const sessionToken = localStorage.getItem('auth_hint');
          const resp = await axios.get(
            `${API_BASE_URL}/api/photos/${photoDriveId}?thumbnail=true`,
            {
              headers: { Authorization: `Bearer ${sessionToken}` },
              responseType: 'blob',
            }
          );
          if (!cancelled) {
            localBlobUrl = URL.createObjectURL(resp.data);
            setSrc(localBlobUrl);
          }
        }
      } catch (error) {
        console.debug('CareLog photo fetch failed', error);
      }
    })();
    return () => {
      cancelled = true;
      if (localBlobUrl) URL.revokeObjectURL(localBlobUrl);
    };
  }, [photoDriveId, isLocalMode]);
  return src;
}

/** Thumbnail cell used in both the growth strip and the timeline row. */
const CareLogPhotoThumb = ({ photoDriveId, isLocalMode, size = 40, onClick, alt = '' }) => {
  const src = useCareLogPhoto(photoDriveId, isLocalMode);
  const style = { width: size, height: size, borderRadius: 6, overflow: 'hidden', flexShrink: 0 };
  if (!src) {
    return (
      <div style={{ ...style, background: 'hsl(143, 12%, 90%)' }} aria-hidden="true">
        <Loader2 className="w-3 h-3 animate-spin" style={{ margin: 'auto', display: 'block', marginTop: size / 2 - 6, color: 'hsl(143, 12%, 55%)' }} />
      </div>
    );
  }
  // Render a plain div when no onClick — prevents nested-<button> DOM
  // errors when this thumb sits inside a parent button (compare picker).
  // Also drop aria-label here: ARIA doesn't allow labels on generic elements,
  // and the surrounding parent-button carries the accessible name.
  if (!onClick) {
    return (
      <div
        style={{ ...style, border: '1px solid hsl(143, 12%, 82%)' }}
        data-testid="care-log-thumb"
      >
        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ ...style, padding: 0, border: '1px solid hsl(143, 12%, 82%)', cursor: 'pointer' }}
      aria-label={alt || 'Open photo'}
      data-testid="care-log-thumb"
    >
      {/* Inner img alt is empty — the button's aria-label is the name;
          duplicating it makes screen readers announce twice. */}
      <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    </button>
  );
};

export const CareLog = ({ plantId, plant, careLog, isLocalMode, onLogChange, onCoverPromoted }) => {
  const { t, language } = useLanguage();
  const [busy, setBusy] = useState(false);
  // The care-log entry that the user is attaching a photo to. Held so the
  // hidden file inputs know which entry to update on selection.
  const [attachingEntryId, setAttachingEntryId] = useState(null);
  const [showChooser, setShowChooser] = useState(false);
  // Enlarged photo view — { entryId, photoDriveId, action } | null
  const [enlarged, setEnlarged] = useState(null);
  // Track the trigger element that opened the enlarged dialog so we can
  // restore keyboard focus to it on close (Radix can't do this on its own
  // when a Dialog is conditionally mounted without a DialogTrigger).
  const enlargeTriggerRef = useRef(null);
  // Wraps setEnlarged so every enlarge action captures its trigger.
  const openEnlarged = (payload, event) => {
    enlargeTriggerRef.current = event?.currentTarget || null;
    setEnlarged(payload);
  };
  const closeEnlarged = () => {
    setEnlarged(null);
    setCompareWith(null);
    setShowCompareList(false);
    // Restore focus after Radix finishes unmounting.
    const trigger = enlargeTriggerRef.current;
    if (trigger && typeof trigger.focus === 'function') {
      setTimeout(() => trigger.focus(), 0);
    }
    enlargeTriggerRef.current = null;
  };
  // Inline note editing on a timeline row.
  const [editingNoteFor, setEditingNoteFor] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  // Before/after compare mode inside the enlarged view. Holds the SECOND
  // entry being compared to the currently-enlarged one. `showCompareList`
  // toggles the little picker.
  const [compareWith, setCompareWith] = useState(null);
  const [showCompareList, setShowCompareList] = useState(false);
  // PDF export busy state.
  const [exporting, setExporting] = useState(false);
  // Native share busy state (PNG collage → OS share sheet).
  const [sharing, setSharing] = useState(false);
  // Growth strip sort order — 'asc' (oldest → newest, default) or 'desc'.
  // Persisted per browser so users don't have to re-toggle across sessions.
  const [stripSort, setStripSort] = useState(() => {
    try {
      return localStorage.getItem('care_log_strip_sort') === 'desc' ? 'desc' : 'asc';
    } catch (_e) {
      return 'asc';
    }
  });
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const log = Array.isArray(careLog)
    ? [...careLog].sort((a, b) => (b.at || '').localeCompare(a.at || ''))
    : [];
  // Growth strip: chronological (default asc = oldest → newest, time-lapse
  // feel) OR reverse-chrono (desc = newest first) driven by `stripSort`.
  // Only entries with a photo attached appear here.
  const photoEntries = [...(careLog || [])]
    .filter((e) => e && e.photo_drive_id)
    .sort((a, b) => {
      const cmp = (a.at || '').localeCompare(b.at || '');
      return stripSort === 'desc' ? -cmp : cmp;
    });

  const addEntry = async (action) => {
    if (busy) return;
    try {
      setBusy(true);
      let updatedLog;
      if (isLocalMode) {
        updatedLog = await addLocalCareLogEntry(plantId, action);
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        const resp = await axios.post(
          `${API_BASE_URL}/api/plants/${plantId}/care-log`,
          { action },
          { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 20000 }
        );
        updatedLog = resp.data?.care_log || [];
      }
      onLogChange(updatedLog);
      toast.success(t(`careLog.actionDone.${action}`) || `Logged: ${action}`);
    } catch (error) {
      console.error('Add care log entry failed:', error);
      toast.error(t('careLog.addFailed') || 'Failed to log — try again.');
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (entryId) => {
    if (busy) return;
    try {
      setBusy(true);
      let updatedLog;
      if (isLocalMode) {
        updatedLog = await deleteLocalCareLogEntry(plantId, entryId);
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        const resp = await axios.delete(
          `${API_BASE_URL}/api/plants/${plantId}/care-log/${entryId}`,
          { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 20000 }
        );
        updatedLog = resp.data?.care_log || [];
      }
      onLogChange(updatedLog);
    } catch (error) {
      console.error('Delete care log entry failed:', error);
      toast.error(t('careLog.deleteFailed') || 'Failed to undo — try again.');
    } finally {
      setBusy(false);
    }
  };

  // Opens the camera/gallery chooser sheet for a given entry. Two hidden
  // file inputs handle the actual pick — one with `capture="environment"`
  // that opens the mobile OS camera directly, one plain gallery pick.
  const startAttachPhoto = (entryId) => {
    if (busy) return;
    setAttachingEntryId(entryId);
    setShowChooser(true);
  };

  const pickFromCamera = () => {
    setShowChooser(false);
    cameraInputRef.current?.click();
  };

  const pickFromGallery = () => {
    setShowChooser(false);
    galleryInputRef.current?.click();
  };

  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    const entryId = attachingEntryId;
    // Always clear the input value so the same file can be re-selected.
    if (e.target) e.target.value = '';
    if (!file || !entryId) { setAttachingEntryId(null); return; }
    try {
      setBusy(true);
      const reader = new FileReader();
      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      let updatedLog;
      if (isLocalMode) {
        updatedLog = await attachLocalCareLogPhoto(plantId, entryId, base64);
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        const resp = await axios.post(
          `${API_BASE_URL}/api/plants/${plantId}/care-log/${entryId}/photo`,
          { image_base64: base64 },
          { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 60000 }
        );
        updatedLog = resp.data?.care_log || [];
      }
      onLogChange(updatedLog);
      toast.success(t('careLog.photoAdded') || 'Photo added to entry');
    } catch (error) {
      console.error('Attach care log photo failed:', error);
      const msg = error?.response?.data?.detail || t('careLog.photoFailed') || 'Failed to attach photo';
      toast.error(msg);
    } finally {
      setBusy(false);
      setAttachingEntryId(null);
    }
  };

  // Save an inline note edit for a given entry.
  const saveNote = async (entryId) => {
    if (busy) return;
    const draft = (noteDraft || '').trim().slice(0, 140);
    try {
      setBusy(true);
      let updatedLog;
      if (isLocalMode) {
        updatedLog = await editLocalCareLogEntryNote(plantId, entryId, draft);
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        const resp = await axios.patch(
          `${API_BASE_URL}/api/plants/${plantId}/care-log/${entryId}`,
          { note: draft || null },
          { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 20000 }
        );
        updatedLog = resp.data?.care_log || [];
      }
      onLogChange(updatedLog);
      setEditingNoteFor(null);
      setNoteDraft('');
    } catch (error) {
      console.error('Edit note failed:', error);
      toast.error(t('careLog.noteEditFailed') || 'Failed to save note — try again.');
    } finally {
      setBusy(false);
    }
  };

  // Copy the currently-enlarged care log photo into the plant's cover
  // carousel. The care log entry keeps its photo intact — this creates a
  // new independent Drive file / IndexedDB record.
  //
  // UX detail: on success we (1) flip the button to a "✓ Done" state so the
  // user gets instant tactile feedback even before the toast is noticed, and
  // (2) auto-close the enlarged dialog after a short beat so the updated
  // cover in the plant's carousel underneath becomes visible. Without this
  // the enlarged view would keep showing the same care log photo, giving the
  // false impression that nothing happened even though the backend updated.
  const [justPromotedEntryId, setJustPromotedEntryId] = useState(null);
  const promoteToCover = async (entry) => {
    if (busy || !entry?.entry_id) return;
    try {
      setBusy(true);
      if (isLocalMode) {
        await promoteLocalCareLogPhotoToCover(plantId, entry.entry_id);
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        await axios.post(
          `${API_BASE_URL}/api/plants/${plantId}/care-log/${entry.entry_id}/promote-cover`,
          {},
          { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 30000 }
        );
      }
      toast.success(t('careLog.setAsCoverDone') || 'Set as cover photo');
      setJustPromotedEntryId(entry.entry_id);
      onCoverPromoted?.();
      // Small beat so the user actually sees the "✓ Done" flip and the toast
      // before the dialog dismisses. 700ms feels responsive but not abrupt.
      setTimeout(() => {
        closeEnlarged();
        setJustPromotedEntryId(null);
      }, 700);
    } catch (error) {
      console.error('Promote cover failed:', error);
      const msg = error?.response?.data?.detail || error?.message || t('careLog.setAsCoverFailed') || 'Failed to set as cover';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // Resolve a care log photo to a usable src URL for the PDF export.
  // Kept out of the render tree since it fires on-demand (button click).
  const resolvePhotoSrcForPdf = async (entry) => {
    if (isLocalMode) {
      const base64 = await getLocalPhoto(entry.photo_drive_id);
      return base64 ? `data:image/jpeg;base64,${base64}` : null;
    }
    const sessionToken = localStorage.getItem('auth_hint');
    const resp = await axios.get(`${API_BASE_URL}/api/photos/${entry.photo_drive_id}`, {
      headers: { Authorization: `Bearer ${sessionToken}` },
      responseType: 'blob',
    });
    // Convert blob to base64 data URL so html2canvas doesn't have to deal
    // with revocable blob URLs mid-render.
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(resp.data);
    });
  };

  const handleExportTimeline = async () => {
    if (exporting || photoEntries.length === 0) return;
    try {
      setExporting(true);
      toast.loading(t('careLog.exporting') || 'Preparing timeline PDF…', { id: 'care-timeline-pdf' });
      await exportCareTimelineToPDF({
        plant: { common_name: plant?.common_name, scientific_name: plant?.scientific_name },
        entries: photoEntries,
        resolvePhotoSrc: resolvePhotoSrcForPdf,
      });
      toast.success(t('careLog.exportDone') || 'Timeline exported', { id: 'care-timeline-pdf' });
    } catch (error) {
      console.error('Timeline PDF export failed:', error);
      toast.error(error?.message || t('careLog.exportFailed') || 'Failed to export timeline', { id: 'care-timeline-pdf' });
    } finally {
      setExporting(false);
    }
  };

  // Native share the growth strip as a PNG collage. On mobile PWAs this
  // opens the OS share sheet; on desktop it falls back to a PNG download.
  const handleShareTimeline = async () => {
    if (sharing || photoEntries.length === 0) return;
    try {
      setSharing(true);
      toast.loading(t('careLog.sharing') || 'Preparing timeline image…', { id: 'care-timeline-share' });
      const { shared, downloaded } = await shareCareTimeline({
        plant: { common_name: plant?.common_name, scientific_name: plant?.scientific_name },
        entries: photoEntries,
        resolvePhotoSrc: resolvePhotoSrcForPdf,
      });
      if (shared) {
        toast.success(t('careLog.shareDone') || 'Shared timeline', { id: 'care-timeline-share' });
      } else if (downloaded) {
        toast.success(t('careLog.downloaded') || 'Timeline image downloaded', { id: 'care-timeline-share' });
      } else {
        // User cancelled the share sheet — dismiss quietly.
        toast.dismiss('care-timeline-share');
      }
    } catch (error) {
      console.error('Timeline share failed:', error);
      toast.error(error?.message || t('careLog.shareFailed') || 'Failed to share timeline', { id: 'care-timeline-share' });
    } finally {
      setSharing(false);
    }
  };

  // Flip the growth strip between asc (oldest → newest, time-lapse) and
  // desc (newest first). Preference persisted so users don't have to
  // re-toggle across sessions.
  const toggleStripSort = () => {
    const next = stripSort === 'asc' ? 'desc' : 'asc';
    setStripSort(next);
    try { localStorage.setItem('care_log_strip_sort', next); } catch (_e) { /* quota */ }
  };


  return (
    <div className="mt-4 space-y-3" data-testid="care-log">
      <h3 className="text-base font-semibold" style={{ color: 'hsl(143, 28%, 25%)' }}>
        {t('careLog.title') || 'Care Log'}
      </h3>

      {/* Quick-tick action buttons: one tap = one dated entry. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="care-log-actions">
        {ACTIONS.map(({ key, icon: Icon, colorFg, colorBg }) => (
          <button
            key={key}
            type="button"
            onClick={() => addEntry(key)}
            disabled={busy}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all hover:brightness-95 disabled:opacity-50"
            style={{ backgroundColor: colorBg, color: colorFg, border: `1px solid ${colorBg}` }}
            aria-label={t(`careLog.action.${key}`) || key}
            data-testid={`care-log-action-${key}`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" aria-hidden="true" />}
            <span>{t(`careLog.action.${key}`) || key}</span>
          </button>
        ))}
      </div>

      {/* Growth timeline — horizontal-scroll strip of every photo the user
          attached to a care-log entry. Order controlled by `stripSort`
          (default asc = time-lapse feel). Only shows when there are 2+
          photos, otherwise the inline timeline thumb is enough. */}
      {photoEntries.length >= 2 && (
        <div className="space-y-1.5" data-testid="care-log-growth-strip">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs font-medium" style={{ color: 'hsl(143, 28%, 35%)' }}>
              {t('careLog.growthTimeline') || 'Growth timeline'}
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={toggleStripSort}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded transition-colors"
                style={{ color: 'hsl(143, 28%, 30%)', border: '1px solid hsl(143, 28%, 82%)', backgroundColor: 'white' }}
                aria-label={stripSort === 'asc' ? (t('careLog.sortNewestFirst') || 'Sort newest first') : (t('careLog.sortOldestFirst') || 'Sort oldest first')}
                title={stripSort === 'asc' ? (t('careLog.sortNewestFirst') || 'Sort newest first') : (t('careLog.sortOldestFirst') || 'Sort oldest first')}
                data-testid="care-log-strip-sort-toggle"
                data-sort={stripSort}
              >
                <ArrowUpDown className="w-3 h-3" aria-hidden="true" />
                <span>{stripSort === 'asc' ? (t('careLog.oldestFirst') || 'Oldest first') : (t('careLog.newestFirst') || 'Newest first')}</span>
              </button>
              <button
                type="button"
                onClick={handleShareTimeline}
                disabled={sharing || exporting}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded transition-colors disabled:opacity-50"
                style={{ color: 'hsl(143, 28%, 30%)', border: '1px solid hsl(143, 28%, 82%)', backgroundColor: 'white' }}
                aria-label={t('careLog.shareTimeline') || 'Share timeline'}
                data-testid="care-log-share-timeline"
              >
                {sharing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" aria-hidden="true" />}
                <span>{t('careLog.share') || 'Share'}</span>
              </button>
              <button
                type="button"
                onClick={handleExportTimeline}
                disabled={exporting || sharing}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded transition-colors disabled:opacity-50"
                style={{ color: 'hsl(143, 28%, 30%)', border: '1px solid hsl(143, 28%, 82%)', backgroundColor: 'white' }}
                aria-label={t('careLog.exportTimeline') || 'Export timeline as PDF'}
                data-testid="care-log-export-timeline"
              >
                {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" aria-hidden="true" />}
                <span>{t('careLog.exportTimeline') || 'Export PDF'}</span>
              </button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
            {photoEntries.map((entry) => {
              const meta = ACTIONS.find((a) => a.key === entry.action) || ACTIONS[0];
              return (
                <div
                  key={entry.entry_id}
                  className="flex-shrink-0 text-center"
                  data-testid={`care-log-growth-cell-${entry.entry_id}`}
                >
                  <CareLogPhotoThumb
                    photoDriveId={entry.photo_drive_id}
                    isLocalMode={isLocalMode}
                    size={80}
                    onClick={(e) => openEnlarged({ entryId: entry.entry_id, photoDriveId: entry.photo_drive_id, action: entry.action, at: entry.at, note: entry.note }, e)}
                    alt={`${t(`careLog.actionPast.${entry.action}`) || entry.action} photo, ${new Date(entry.at).toLocaleDateString(language, { year: 'numeric', month: 'short', day: 'numeric' })}`}
                  />
                  <p className="text-[10px] mt-1" style={{ color: meta.colorFg }}>
                    {t(`careLog.actionPast.${entry.action}`) || entry.action}
                  </p>
                  <p className="text-[10px]" style={{ color: 'hsl(125, 12%, 35%)' }}>
                    {new Date(entry.at).toLocaleDateString(language, { month: 'short', day: 'numeric' })}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Timeline — reverse chrono of the 10 most recent entries. */}
      {log.length === 0 ? (
        <p className="text-xs italic" style={{ color: 'hsl(125, 12%, 50%)' }} data-testid="care-log-empty">
          {t('careLog.empty') || 'No care events yet — tap a button above to log the first one.'}
        </p>
      ) : (
        <ul className="space-y-1.5" data-testid="care-log-timeline">
          {log.slice(0, 10).map((entry) => {
            const meta = ACTIONS.find((a) => a.key === entry.action) || ACTIONS[0];
            const Icon = meta.icon;
            const hasPhoto = !!entry.photo_drive_id;
            const isEditingNote = editingNoteFor === entry.entry_id;
            return (
              <li
                key={entry.entry_id}
                className="flex flex-col gap-1 py-1 px-2 rounded text-sm"
                style={{ backgroundColor: 'hsl(82, 55%, 97%)' }}
                data-testid={`care-log-entry-${entry.entry_id}`}
              >
                <div className="flex items-center gap-2">
                  {hasPhoto ? (
                    <CareLogPhotoThumb
                      photoDriveId={entry.photo_drive_id}
                      isLocalMode={isLocalMode}
                      size={36}
                      onClick={(e) => openEnlarged({ entryId: entry.entry_id, photoDriveId: entry.photo_drive_id, action: entry.action, at: entry.at, note: entry.note }, e)}
                      alt={`${t(`careLog.actionPast.${entry.action}`) || entry.action} photo, ${new Date(entry.at).toLocaleDateString(language, { year: 'numeric', month: 'short', day: 'numeric' })}`}
                    />
                  ) : (
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: meta.colorFg }} aria-hidden="true" />
                  )}
                  <span className="flex-1 min-w-0" style={{ color: 'hsl(143, 28%, 25%)' }}>
                    {t(`careLog.actionPast.${entry.action}`) || entry.action}
                  </span>
                  <span className="text-xs" style={{ color: 'hsl(125, 12%, 35%)' }}>
                    {relTime(entry.at, language)}
                  </span>
                  {!hasPhoto && (
                    <button
                      type="button"
                      onClick={() => startAttachPhoto(entry.entry_id)}
                      disabled={busy}
                      className="p-1 rounded hover:bg-white transition-colors disabled:opacity-50"
                      style={{ color: 'hsl(143, 28%, 40%)' }}
                      aria-label={t('careLog.attachPhoto') || 'Attach photo'}
                      data-testid={`care-log-attach-photo-${entry.entry_id}`}
                    >
                      <ImagePlus className="w-3.5 h-3.5" aria-hidden="true" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (isEditingNote) {
                        setEditingNoteFor(null);
                        setNoteDraft('');
                      } else {
                        setEditingNoteFor(entry.entry_id);
                        setNoteDraft(entry.note || '');
                      }
                    }}
                    className="p-1 rounded hover:bg-white transition-colors"
                    style={{ color: 'hsl(143, 28%, 40%)' }}
                    aria-label={t('careLog.editNote') || 'Edit note'}
                    data-testid={`care-log-edit-note-${entry.entry_id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeEntry(entry.entry_id)}
                    className="p-0.5 rounded hover:bg-white transition-colors"
                    style={{ color: 'hsl(125, 12%, 45%)' }}
                    aria-label={t('careLog.undo') || 'Undo'}
                    data-testid={`care-log-undo-${entry.entry_id}`}
                  >
                    <X className="w-3 h-3" aria-hidden="true" />
                  </button>
                </div>

                {/* Inline note editor OR note display line */}
                {isEditingNote ? (
                  <div className="flex items-center gap-1.5 pl-6" data-testid={`care-log-note-editor-${entry.entry_id}`}>
                    <input
                      type="text"
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value.slice(0, 140))}
                      onKeyDown={(e) => {
                        // Stop keys from bubbling to the Radix Dialog, which
                        // treats Escape as "close the whole plant detail
                        // modal" and Enter as "submit the parent form".
                        // Otherwise a keyboard user cancelling a mistyped
                        // note loses the entire modal (a11y: WCAG 3.3.4).
                        if (e.key === 'Enter') {
                          e.stopPropagation();
                          saveNote(entry.entry_id);
                        }
                        if (e.key === 'Escape') {
                          e.stopPropagation();
                          e.preventDefault();
                          setEditingNoteFor(null);
                          setNoteDraft('');
                        }
                      }}
                      placeholder={t('careLog.notePlaceholder') || 'e.g. first new leaf!'}
                      maxLength={140}
                      className="flex-1 min-w-0 text-xs px-2 py-1 rounded border"
                      style={{ borderColor: 'hsl(143, 28%, 75%)', color: 'hsl(143, 28%, 20%)', backgroundColor: 'white' }}
                      autoFocus
                      data-testid={`care-log-note-input-${entry.entry_id}`}
                    />
                    <button
                      type="button"
                      onClick={() => saveNote(entry.entry_id)}
                      disabled={busy}
                      className="p-1 rounded transition-colors disabled:opacity-50"
                      style={{ backgroundColor: 'hsl(143, 28%, 45%)', color: 'white' }}
                      aria-label={t('save') || 'Save'}
                      data-testid={`care-log-note-save-${entry.entry_id}`}
                    >
                      <Check className="w-3 h-3" aria-hidden="true" />
                    </button>
                  </div>
                ) : entry.note ? (
                  <p
                    className="text-xs italic pl-6 break-words"
                    style={{ color: 'hsl(125, 12%, 40%)', overflowWrap: 'anywhere' }}
                    data-testid={`care-log-note-display-${entry.entry_id}`}
                  >
                    {entry.note}
                  </p>
                ) : null}
              </li>
            );
          })}
          {log.length > 10 && (
            <li className="text-xs text-center pt-1" style={{ color: 'hsl(125, 12%, 55%)' }}>
              {(t('careLog.moreEntries') || '{count} older entries').replace('{count}', log.length - 10)}
            </li>
          )}
        </ul>
      )}

      {/* Hidden file inputs — one is capture=environment (mobile OS camera),
          one is plain gallery. The chooser dialog above picks which fires. */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        onChange={handlePhotoSelected}
        className="hidden"
        data-testid="care-log-photo-gallery-input"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handlePhotoSelected}
        className="hidden"
        data-testid="care-log-photo-camera-input"
      />

      {/* Camera / Gallery chooser sheet */}
      <Dialog open={showChooser} onOpenChange={(open) => { if (!open) { setShowChooser(false); setAttachingEntryId(null); } }}>
        <DialogContent className="max-w-sm" data-testid="care-log-photo-chooser">
          <DialogHeader>
            <DialogTitle style={{ color: 'hsl(143, 28%, 25%)' }}>
              {t('careLog.attachPhoto') || 'Attach a photo'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              type="button"
              onClick={pickFromCamera}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors"
              style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
              data-testid="care-log-photo-camera-btn"
            >
              <Camera className="w-6 h-6" aria-hidden="true" />
              <span className="text-sm">{t('fromCamera') || 'From Camera'}</span>
            </button>
            <button
              type="button"
              onClick={pickFromGallery}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors"
              style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
              data-testid="care-log-photo-gallery-btn"
            >
              <ImagePlus className="w-6 h-6" aria-hidden="true" />
              <span className="text-sm">{t('fromGallery') || 'From Gallery'}</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Enlarged photo view — its own Dialog so nested state closes cleanly */}
      {enlarged && (
        <Dialog open={true} onOpenChange={(open) => { if (!open) closeEnlarged(); }}>
          <DialogContent className="max-w-2xl p-0" data-testid="care-log-enlarged">
            <DialogHeader className="px-4 pt-4">
              <DialogTitle style={{ color: 'hsl(143, 28%, 25%)' }}>
                {t(`careLog.actionPast.${enlarged.action}`) || enlarged.action}
                {' · '}
                <span className="text-sm font-normal" style={{ color: 'hsl(125, 12%, 35%)' }}>
                  {new Date(enlarged.at).toLocaleDateString(language, { year: 'numeric', month: 'short', day: 'numeric' })}
                </span>
              </DialogTitle>
            </DialogHeader>

            <div className="px-4 pt-2 pb-4 space-y-3">
              {/* Action toolbar — Compare / Set as cover. Both only relevant
                  when there are 2+ care photos (Compare) or photos at all
                  (Set as cover). */}
              <div className="flex flex-wrap gap-2" data-testid="care-log-enlarged-actions">
                {photoEntries.length >= 2 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (compareWith) {
                        // Exit compare mode entirely — clear the second
                        // photo AND close the picker.
                        setCompareWith(null);
                        setShowCompareList(false);
                      } else {
                        setShowCompareList((v) => !v);
                      }
                    }}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded transition-colors"
                    style={{
                      backgroundColor: compareWith ? 'hsl(200, 60%, 45%)' : 'white',
                      color: compareWith ? 'white' : 'hsl(143, 28%, 30%)',
                      border: '1px solid hsl(143, 28%, 82%)',
                    }}
                    data-testid="care-log-compare-toggle"
                  >
                    <Split className="w-3.5 h-3.5" aria-hidden="true" />
                    {compareWith ? (t('careLog.stopCompare') || 'Stop compare') : (t('careLog.compare') || 'Compare')}
                  </button>
                )}
                <button
                  type="button"
                  // aria-disabled + onClick guard rather than native `disabled`
                  // so the button keeps focus during the 700ms "Cover set"
                  // window — a disabled control loses focus, and screen
                  // readers won't announce label changes on a defocused
                  // control. The onClick guard makes it inert while busy.
                  onClick={() => {
                    if (busy || justPromotedEntryId === enlarged.entryId) return;
                    promoteToCover({ entry_id: enlarged.entryId });
                  }}
                  aria-disabled={busy || justPromotedEntryId === enlarged.entryId}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded transition-colors"
                  style={
                    justPromotedEntryId === enlarged.entryId
                      // Success confirmation — full opacity + WCAG-AA-safe
                      // dark green so the "Cover set" ✓ label stays readable
                      // (previous hsl(143,45%,40%) with disabled:opacity-70
                      // fell to 2.43:1 in a11y audit).
                      ? { backgroundColor: 'hsl(143, 45%, 34%)', color: 'white', border: '1px solid hsl(143, 45%, 34%)' }
                      : { backgroundColor: 'white', color: 'hsl(143, 28%, 30%)', border: '1px solid hsl(143, 28%, 82%)', opacity: busy ? 0.7 : 1 }
                  }
                  data-testid="care-log-set-cover"
                >
                  {justPromotedEntryId === enlarged.entryId ? (
                    <>
                      <Check className="w-3.5 h-3.5" aria-hidden="true" />
                      {t('careLog.setAsCoverDoneShort') || 'Cover set'}
                    </>
                  ) : (
                    <>
                      <Star className="w-3.5 h-3.5" aria-hidden="true" />
                      {t('careLog.setAsCover') || 'Set as cover'}
                    </>
                  )}
                </button>
              </div>

              {/* Compare picker — appears when Compare is toggled on but no
                  second photo has been chosen yet. Small horizontal list of
                  the OTHER photo-tagged entries. */}
              {showCompareList && !compareWith && (
                <div
                  className="p-2 rounded space-y-2"
                  style={{ backgroundColor: 'hsl(200, 60%, 97%)', border: '1px solid hsl(200, 60%, 85%)' }}
                  data-testid="care-log-compare-picker"
                >
                  <p className="text-xs" style={{ color: 'hsl(200, 60%, 30%)' }}>
                    {t('careLog.comparePickPrompt') || 'Pick a photo to compare with:'}
                  </p>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {photoEntries
                      .filter((e) => e.entry_id !== enlarged.entryId)
                      .map((e) => {
                        const dateStr = new Date(e.at).toLocaleDateString(language, { year: 'numeric', month: 'short', day: 'numeric' });
                        const actionStr = t(`careLog.actionPast.${e.action}`) || e.action;
                        return (
                        <button
                          key={e.entry_id}
                          type="button"
                          onClick={() => {
                            setCompareWith(e);
                            setShowCompareList(false);
                          }}
                          className="flex-shrink-0 text-center"
                          aria-label={(t('careLog.comparePickAria') || 'Compare with {action} photo from {date}')
                            .replace('{action}', actionStr).replace('{date}', dateStr)}
                          data-testid={`care-log-compare-pick-${e.entry_id}`}
                        >
                          <CareLogPhotoThumb
                            photoDriveId={e.photo_drive_id}
                            isLocalMode={isLocalMode}
                            size={56}
                            alt=""
                          />
                          <p className="text-[9px] mt-0.5" style={{ color: 'hsl(125, 12%, 35%)' }}>
                            {new Date(e.at).toLocaleDateString(language, { month: 'short', day: 'numeric' })}
                          </p>
                        </button>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Main viewer: either the single enlarged photo OR the
                  before/after slider if a compareWith entry is selected. */}
              {compareWith ? (
                <BeforeAfterSlider
                  beforeEntry={
                    // Order chronologically — older photo on "before" side,
                    // newer on "after" side, regardless of which was picked.
                    (enlarged.at || '') < (compareWith.at || '')
                      ? { photoDriveId: enlarged.photoDriveId, at: enlarged.at, action: enlarged.action }
                      : { photoDriveId: compareWith.photo_drive_id, at: compareWith.at, action: compareWith.action }
                  }
                  afterEntry={
                    (enlarged.at || '') < (compareWith.at || '')
                      ? { photoDriveId: compareWith.photo_drive_id, at: compareWith.at, action: compareWith.action }
                      : { photoDriveId: enlarged.photoDriveId, at: enlarged.at, action: enlarged.action }
                  }
                  isLocalMode={isLocalMode}
                  language={language}
                />
              ) : (
                <CareLogEnlargedImage photoDriveId={enlarged.photoDriveId} isLocalMode={isLocalMode} />
              )}

              {enlarged.note && (
                <p
                  className="text-sm italic mt-2 break-words"
                  style={{ color: 'hsl(125, 12%, 35%)', overflowWrap: 'anywhere' }}
                  data-testid="care-log-enlarged-note"
                >
                  {enlarged.note}
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Separated so the full-size fetch happens ONLY when the enlarged dialog
// actually opens — otherwise every thumb would double-fetch to a full-res
// image behind the scenes.
const CareLogEnlargedImage = ({ photoDriveId, isLocalMode }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    (async () => {
      try {
        if (isLocalMode) {
          const base64 = await getLocalPhoto(photoDriveId);
          if (!cancelled && base64) setSrc(`data:image/jpeg;base64,${base64}`);
        } else {
          const sessionToken = localStorage.getItem('auth_hint');
          const resp = await axios.get(`${API_BASE_URL}/api/photos/${photoDriveId}`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            responseType: 'blob',
          });
          if (!cancelled) {
            blobUrl = URL.createObjectURL(resp.data);
            setSrc(blobUrl);
          }
        }
      } catch (error) {
        console.debug('CareLog enlarged photo fetch failed', error);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [photoDriveId, isLocalMode]);
  if (!src) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'hsl(143, 28%, 45%)' }} />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 6 }}
      data-testid="care-log-enlarged-image"
    />
  );
};

// Two-thumb before/after slider — CSS clip-path on the "after" layer clipped
// by the divider position (0-100 %). No external lib. Works with mouse,
// touch, and keyboard (arrow keys). Both images render at 100% width;
// container aspect matches the naturally-loaded "before" image so
// proportions stay honest.
const BeforeAfterSlider = ({ beforeEntry, afterEntry, isLocalMode, language }) => {
  const beforeSrc = useCareLogPhoto(beforeEntry.photoDriveId, isLocalMode);
  const afterSrc = useCareLogPhoto(afterEntry.photoDriveId, isLocalMode);
  const [pos, setPos] = useState(50); // percent
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const raw = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, raw)));
  };
  const onPointerDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    handleMove(e.clientX);
  };
  const onPointerMove = (e) => {
    if (e.buttons !== 1 && e.pointerType === 'mouse') return;
    handleMove(e.clientX);
  };
  // Keyboard a11y: arrow keys nudge by 5%, PageUp/PageDown by 25%,
  // Home/End jump to endpoints. Matches the standard ARIA slider pattern.
  const onKeyDown = (e) => {
    let next = pos;
    switch (e.key) {
      case 'ArrowLeft': next = pos - 5; break;
      case 'ArrowRight': next = pos + 5; break;
      case 'PageDown': next = pos - 25; break;
      case 'PageUp': next = pos + 25; break;
      case 'Home': next = 0; break;
      case 'End': next = 100; break;
      default: return;
    }
    e.preventDefault();
    setPos(Math.max(0, Math.min(100, next)));
  };

  if (!beforeSrc || !afterSrc) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'hsl(143, 28%, 45%)' }} />
      </div>
    );
  }

  const fmt = (iso) => new Date(iso).toLocaleDateString(language, { month: 'short', day: 'numeric', year: 'numeric' });
  const ariaLabel = `Before ${fmt(beforeEntry.at)} and after ${fmt(afterEntry.at)} comparison`;

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className="relative w-full select-none touch-none focus:outline-none focus:ring-2"
      style={{ borderRadius: 6, overflow: 'hidden', maxHeight: '70vh', backgroundColor: 'hsl(143, 12%, 90%)' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onKeyDown={onKeyDown}
      role="slider"
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos)}
      aria-valuetext={`${Math.round(pos)}% ${afterEntry.at > beforeEntry.at ? 'after' : 'before'}`}
      data-testid="care-log-compare-slider"
    >
      <img src={beforeSrc} alt="before" style={{ width: '100%', display: 'block', maxHeight: '70vh', objectFit: 'contain' }} />
      <img
        src={afterSrc}
        alt="after"
        style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          objectFit: 'contain', maxHeight: '70vh',
          clipPath: `inset(0 0 0 ${pos}%)`,
        }}
      />
      {/* Divider */}
      <div
        style={{
          position: 'absolute', top: 0, bottom: 0, left: `${pos}%`,
          width: 2, background: 'white', boxShadow: '0 0 6px rgba(0,0,0,0.35)',
          transform: 'translateX(-1px)',
          pointerEvents: 'none',
        }}
      />
      {/* Draggable handle */}
      <div
        style={{
          position: 'absolute', top: '50%', left: `${pos}%`,
          transform: 'translate(-50%, -50%)',
          width: 32, height: 32, borderRadius: '50%',
          background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Split className="w-4 h-4" style={{ color: 'hsl(143, 28%, 30%)' }} aria-hidden="true" />
      </div>
      {/* Corner date labels */}
      <span
        style={{
          position: 'absolute', top: 8, left: 8,
          padding: '2px 6px', borderRadius: 4,
          background: 'rgba(0,0,0,0.55)', color: 'white',
          fontSize: 10, pointerEvents: 'none',
        }}
      >
        {fmt(beforeEntry.at)}
      </span>
      <span
        style={{
          position: 'absolute', top: 8, right: 8,
          padding: '2px 6px', borderRadius: 4,
          background: 'rgba(0,0,0,0.55)', color: 'white',
          fontSize: 10, pointerEvents: 'none',
        }}
      >
        {fmt(afterEntry.at)}
      </span>
    </div>
  );
};
